import React from "react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEdit2, FiXCircle } from "react-icons/fi";
import { HiOutlineChevronUp, HiOutlineChevronDown } from "react-icons/hi2";
import Skeleton from "../Loader/Skeleton";

const EDITABLE_STATUSES = ["DRAFT", "SCHEDULED"];
const CANCELLABLE_STATUSES = ["DRAFT", "SCHEDULED", "QUEUED"];

const STATUS_COLORS = {
  DRAFT: { bg: "#F3F4F6", color: "#374151", border: "#D1D5DB" },
  SCHEDULED: { bg: "#EFF6FF", color: "#2563EB", border: "#BFDBFE" },
  QUEUED: { bg: "#F5F3FF", color: "#7C3AED", border: "#DDD6FE" },
  RUNNING: { bg: "#FFFBEB", color: "#B45309", border: "#FDE68A" },
  COMPLETED: { bg: "#E6F9F0", color: "#27AE60", border: "#86EFAC" },
  PARTIAL_SUCCESS: { bg: "#FFF7ED", color: "#C2410C", border: "#FED7AA" },
  FAILED: { bg: "#FFF3F3", color: "#C0335A", border: "#FCA5A5" },
  CANCELLED: { bg: "#F3F4F6", color: "#6B7280", border: "#D1D5DB" },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.DRAFT;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 12px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "600",
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {status.replace("_", " ")}
    </span>
  );
};

const ChannelBadge = ({ channel }) => {
  const isSms = channel === "SMS";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 12px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "600",
        background: isSms ? "#EFF6FF" : "#E6F9F0",
        color: isSms ? "#2563EB" : "#27AE60",
        border: `1px solid ${isSms ? "#BFDBFE" : "#86EFAC"}`,
      }}
    >
      {isSms ? "SMS" : "WhatsApp"}
    </span>
  );
};

const SortIcon = ({ col, sortBy, sortOrder }) => {
  const active = sortBy === col;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", marginLeft: "4px", verticalAlign: "middle" }}>
      <HiOutlineChevronUp size={10} style={{ color: active && sortOrder === "asc" ? "#C0335A" : "#9CA3AF", marginBottom: "-2px" }} />
      <HiOutlineChevronDown size={10} style={{ color: active && sortOrder === "desc" ? "#C0335A" : "#9CA3AF" }} />
    </span>
  );
};

const getEditDisabledReason = (row) =>
  EDITABLE_STATUSES.includes(row.status) ? null : "Cannot edit once a campaign has started sending";

const getCancelDisabledReason = (row) =>
  CANCELLABLE_STATUSES.includes(row.status) ? null : "Cannot cancel once a campaign has started sending";

const formatDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? "-" : dt.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const CampaignTable = ({
  data,
  loading,
  page,
  limit,
  total,
  sortBy,
  sortOrder,
  onSort,
  onPageChange,
  onLimitChange,
  onView,
  onEdit,
  onCancel,
}) => {
  const navigate = useNavigate();
  const totalPages = Math.ceil(total / limit) || 1;

  const thStyle = {
    background: "#F9FAFB",
    fontWeight: "600",
    fontSize: "13px",
    color: "#374151",
    padding: "12px 16px",
    borderBottom: "1px solid #E5E7EB",
    whiteSpace: "nowrap",
    userSelect: "none",
  };
  const thSortable = { ...thStyle, cursor: "pointer" };
  const tdStyle = {
    padding: "12px 16px",
    fontSize: "14px",
    color: "#374151",
    borderBottom: "1px solid #F3F4F6",
    verticalAlign: "middle",
  };

  const handleSort = (col) => onSort(col, sortBy === col && sortOrder === "asc" ? "desc" : "asc");

  const skeletonRows = Array.from({ length: limit });

  const ActionButtons = ({ row }) => {
    const editReason = getEditDisabledReason(row);
    const cancelReason = getCancelDisabledReason(row);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "nowrap" }}>
        <button
          onClick={() => onView?.(row)}
          title="View details"
          style={{
            background: "#FDE8EF",
            border: "1px solid #C0335A",
            color: "#C0335A",
            borderRadius: "8px",
            padding: "6px 10px",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "13px",
            fontWeight: "600",
          }}
        >
          <FiEye size={14} />
        </button>
        <button
          onClick={editReason ? undefined : () => onEdit?.(row)}
          disabled={!!editReason}
          title={editReason || "Edit campaign"}
          style={{
            background: editReason ? "#F9FAFB" : "#EFF6FF",
            border: `1px solid ${editReason ? "#E5E7EB" : "#BFDBFE"}`,
            color: editReason ? "#9CA3AF" : "#2563EB",
            borderRadius: "8px",
            padding: "6px 10px",
            cursor: editReason ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "13px",
            fontWeight: "600",
            opacity: editReason ? 0.6 : 1,
          }}
        >
          <FiEdit2 size={14} />
        </button>
        <button
          onClick={cancelReason ? undefined : () => onCancel?.(row)}
          disabled={!!cancelReason}
          title={cancelReason || "Cancel campaign"}
          style={{
            background: cancelReason ? "#F9FAFB" : "#FFF3F3",
            border: `1px solid ${cancelReason ? "#E5E7EB" : "#FCA5A5"}`,
            color: cancelReason ? "#9CA3AF" : "#C0335A",
            borderRadius: "8px",
            padding: "6px 10px",
            cursor: cancelReason ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "13px",
            fontWeight: "600",
            opacity: cancelReason ? 0.6 : 1,
          }}
        >
          <FiXCircle size={14} />
        </button>
      </div>
    );
  };

  return (
    <div>
      {/* Desktop table */}
      <div className="campaign-table-desktop" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
          <thead>
            <tr>
              <th style={thStyle}>Sl. No</th>
              <th style={thSortable} onClick={() => handleSort("name")}>
                Name <SortIcon col="name" sortBy={sortBy} sortOrder={sortOrder} />
              </th>
              <th style={thStyle}>Channel</th>
              <th style={thStyle}>Audience</th>
              <th style={thSortable} onClick={() => handleSort("status")}>
                Status <SortIcon col="status" sortBy={sortBy} sortOrder={sortOrder} />
              </th>
              <th style={thStyle}>Sent / Failed</th>
              <th style={thSortable} onClick={() => handleSort("createdAt")}>
                Created <SortIcon col="createdAt" sortBy={sortBy} sortOrder={sortOrder} />
              </th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              skeletonRows.map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 8 }).map((__, j) => (
                    <td key={j} style={tdStyle}>
                      <Skeleton height="16px" marginBottom="0" />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: "#6B7280", padding: "48px" }}>
                  No campaigns found
                </td>
              </tr>
            ) : (
              data.map((row, i) => (
                <tr
                  key={row.id}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  style={{ transition: "background 0.15s" }}
                >
                  <td style={tdStyle}>{(page - 1) * limit + i + 1}</td>
                  <td style={{ ...tdStyle, fontWeight: "600", color: "#111827" }}>{row.name}</td>
                  <td style={tdStyle}><ChannelBadge channel={row.channel} /></td>
                  <td style={tdStyle}>
                    {row.audienceType === "ALL_CUSTOMERS" ? "All Customers" : "Uploaded List"}
                    <span style={{ color: "#6B7280" }}> ({row.recipientCount ?? 0})</span>
                  </td>
                  <td style={tdStyle}><StatusBadge status={row.status} /></td>
                  <td style={tdStyle}>
                    <span style={{ color: "#27AE60", fontWeight: "600" }}>{row.sentCount ?? 0}</span>
                    {" / "}
                    <span style={{ color: "#C0335A", fontWeight: "600" }}>{row.failedCount ?? 0}</span>
                  </td>
                  <td style={tdStyle}>{formatDate(row.createdAt)}</td>
                  <td style={tdStyle}><ActionButtons row={row} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="campaign-table-mobile" style={{ display: "none", padding: "12px" }}>
        {loading
          ? skeletonRows.map((_, i) => (
              <div key={i} style={{ background: "#fff", borderRadius: "12px", padding: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: "12px" }}>
                <Skeleton height="16px" marginBottom="8px" />
                <Skeleton width="60%" height="14px" marginBottom="6px" />
                <Skeleton width="40%" height="14px" />
              </div>
            ))
          : data.map((row) => (
              <div key={row.id} style={{ background: "#fff", borderRadius: "12px", padding: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
                  <div>
                    <div style={{ fontWeight: "600", color: "#111827", fontSize: "15px", marginBottom: "4px" }}>{row.name}</div>
                    <ChannelBadge channel={row.channel} />
                  </div>
                  <StatusBadge status={row.status} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "13px", color: "#6B7280", marginBottom: "12px" }}>
                  <div>
                    <div style={{ marginBottom: "2px" }}>Audience</div>
                    <span style={{ color: "#111827" }}>
                      {row.audienceType === "ALL_CUSTOMERS" ? "All Customers" : "Uploaded List"} ({row.recipientCount ?? 0})
                    </span>
                  </div>
                  <div>
                    <div style={{ marginBottom: "2px" }}>Sent / Failed</div>
                    <span>{row.sentCount ?? 0} / {row.failedCount ?? 0}</span>
                  </div>
                  <div>
                    <div style={{ marginBottom: "2px" }}>Created</div>
                    <span>{formatDate(row.createdAt)}</span>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <ActionButtons row={row} />
                </div>
              </div>
            ))}
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 16px 0", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ fontSize: "13px", color: "#6B7280" }}>
          Showing {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <select
            value={limit}
            onChange={(e) => { onLimitChange(Number(e.target.value)); onPageChange(1); }}
            style={{ border: "1px solid #D1D5DB", borderRadius: "8px", padding: "6px 10px", fontSize: "13px", color: "#374151", cursor: "pointer" }}
          >
            {[10, 20, 50].map((l) => <option key={l} value={l}>{l} per page</option>)}
          </select>
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            style={{ border: "1px solid #D1D5DB", borderRadius: "8px", padding: "6px 14px", fontSize: "13px", background: "#fff", color: page <= 1 ? "#D1D5DB" : "#374151", cursor: page <= 1 ? "not-allowed" : "pointer" }}
          >
            Previous
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let p;
            if (totalPages <= 5) p = i + 1;
            else if (page <= 3) p = i + 1;
            else if (page >= totalPages - 2) p = totalPages - 4 + i;
            else p = page - 2 + i;
            return (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                style={{
                  border: `1px solid ${p === page ? "#C0335A" : "#D1D5DB"}`,
                  borderRadius: "8px",
                  padding: "6px 12px",
                  fontSize: "13px",
                  background: p === page ? "#C0335A" : "#fff",
                  color: p === page ? "#fff" : "#374151",
                  cursor: "pointer",
                  fontWeight: p === page ? "600" : "400",
                  minWidth: "36px",
                }}
              >
                {p}
              </button>
            );
          })}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            style={{ border: "1px solid #D1D5DB", borderRadius: "8px", padding: "6px 14px", fontSize: "13px", background: "#fff", color: page >= totalPages ? "#D1D5DB" : "#374151", cursor: page >= totalPages ? "not-allowed" : "pointer" }}
          >
            Next
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 767px) {
          .campaign-table-desktop { display: none !important; }
          .campaign-table-mobile { display: block !important; }
        }
      `}</style>
    </div>
  );
};

export default CampaignTable;
